import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { STATUS_LABELS, formatDateIT } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import StatusBadge from "@/components/app/StatusBadge";
import { Badge } from "@/components/ui/badge";
import Timeline from "@/components/app/Timeline";
import { toast } from "sonner";
import { ArrowLeft, Copy, ExternalLink, Eye, EyeOff, Factory, FileDown, FileText, Link as LinkIcon, MessageCircle, Package, Paperclip, PenSquare, ShieldCheck, Trash2, Truck, Upload } from "lucide-react";
import { openBinary } from "@/lib/pdfHelpers";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = Object.keys(STATUS_LABELS);

export default function ItemDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [statusDlg, setStatusDlg] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [statusCustomerNote, setStatusCustomerNote] = useState("");

  const [diagDlg, setDiagDlg] = useState(false);
  const [diag, setDiag] = useState({ diagnosis_text: "", proposed_repair: "", warranty_accepted: null, manufacturer_cost: 0, notes: "" });

  const [quoteDlg, setQuoteDlg] = useState(false);
  const [quote, setQuote] = useState({ manufacturer_cost: 0, ama_shipping_cost: 0, ama_labour_cost: 0, other_costs: 0, customer_price_net: 0, vat_rate: 22, expiry_date: "", estimated_days: 7, conditions: "" });

  const [interDlg, setInterDlg] = useState(false);
  const [inter, setInter] = useState({ intervention_type: "repaired", description: "", replacement_serial_number: "", returned_serial_number: "", final_outcome: "funzionante dopo riparazione", condition_after: "" });

  const [attachments, setAttachments] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  const load = async () => { const r = await api.get(`/repairs/items/${id}`); setData(r.data); };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (data?.item?.id) { loadAttachments(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.item?.id]);

  if (!data) return <div className="p-6">Caricamento…</div>;
  const { item, case: c } = data;
  const q = item.quotation;
  const approvalUrl = q?.approval_token ? `${window.location.origin}/approvazione/${q.approval_token}` : null;

  const doStatus = async () => {
    if (!newStatus) return;
    await api.post(`/repairs/items/${item.id}/status`, { to_status: newStatus, note: statusNote, customer_visible_note: statusCustomerNote });
    toast.success("Stato aggiornato");
    setStatusDlg(false); setStatusNote(""); setStatusCustomerNote(""); setNewStatus("");
    load();
  };
  const doDiag = async () => {
    const wasUpdate = !!item.manufacturer_diagnosis;
    await api.post(`/repairs/items/${item.id}/diagnosis`, diag);
    toast.success(wasUpdate ? "Diagnosi aggiornata correttamente" : "Diagnosi registrata correttamente");
    setDiagDlg(false); load();
  };
  const doQuote = async () => {
    const wasUpdate = !!item.quotation;
    await api.post(`/repairs/items/${item.id}/quotation`, quote);
    toast.success(wasUpdate ? "Preventivo aggiornato correttamente" : "Preventivo registrato correttamente");
    setQuoteDlg(false); load();
  };
  const doIntervention = async () => {
    const wasUpdate = !!item.intervention;
    await api.post(`/repairs/items/${item.id}/intervention`, inter);
    toast.success(wasUpdate ? "Intervento aggiornato correttamente" : "Intervento registrato correttamente");
    setInterDlg(false); load();
  };

  const loadAttachments = async () => {
    try {
      const r = await api.get(`/repairs/items/${item.id}/quotation/attachments`);
      setAttachments(r.data);
    } catch { setAttachments([]); }
  };
  const uploadAttachment = async (file, customerVisible) => {
    const ACCEPTED = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!ACCEPTED.includes(file.type) && !/\.(pdf|jpg|jpeg|png|webp|docx)$/i.test(file.name)) {
      toast.error("Formato non supportato. Ammessi: PDF, JPG, PNG, WebP, DOCX.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) { toast.error("File troppo grande (max 15MB)."); return; }
    setAttachmentUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
      });
      await api.post(`/repairs/items/${item.id}/quotation/attachments`, {
        filename: file.name,
        file_type: file.type || (file.name.split(".").pop() || ""),
        data_url: dataUrl,
        display_name: file.name,
        customer_visible: !!customerVisible,
      });
      toast.success("Allegato caricato.");
      await loadAttachments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload allegato");
    } finally { setAttachmentUploading(false); }
  };
  const toggleAttVisibility = async (att) => {
    try {
      await api.patch(`/repairs/items/${item.id}/quotation/attachments/${att.id}`, { customer_visible: !att.customer_visible });
      await loadAttachments();
      toast.success(att.customer_visible ? "Ora è solo interno." : "Ora è visibile al cliente.");
    } catch { toast.error("Errore"); }
  };
  const renameAtt = async (att) => {
    const name = window.prompt("Nome visibile al cliente:", att.display_name || att.filename);
    if (name === null) return;
    try {
      await api.patch(`/repairs/items/${item.id}/quotation/attachments/${att.id}`, { display_name: name });
      await loadAttachments();
      toast.success("Nome aggiornato.");
    } catch { toast.error("Errore"); }
  };
  const deleteAtt = async (att) => {
    if (!window.confirm(`Eliminare l'allegato "${att.display_name || att.filename}"? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/repairs/items/${item.id}/quotation/attachments/${att.id}`);
      await loadAttachments();
      toast.success("Allegato eliminato.");
    } catch { toast.error("Errore eliminazione"); }
  };
  const downloadAtt = async (att) => {
    try {
      const r = await api.get(`/repairs/items/${item.id}/quotation/attachments/${att.id}/download`);
      const link = document.createElement("a"); link.href = r.data.data_url; link.download = r.data.filename; link.click();
    } catch { toast.error("Errore download"); }
  };

  const copyLink = () => {
    if (!approvalUrl) return;
    navigator.clipboard.writeText(approvalUrl); toast.success("Link copiato negli appunti");
  };
  const openWhatsapp = () => {
    if (!approvalUrl) return;
    const msg = `Gentile Cliente, la sua pratica di assistenza AMA Sicurezza ${item.item_number} è in attesa di autorizzazione. Preventivo: ${approvalUrl}`;
    const phone = (c?.delivering_phone || "").replace(/[^0-9]/g, "");
    const link = `https://wa.me/${phone ? phone : ""}?text=${encodeURIComponent(msg)}`;
    window.open(link, "_blank");
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-5" data-testid="item-detail-page">
      <Link to={`/pratiche/${c.id}`} className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Pratica {c.case_number}</Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Prodotto in assistenza</div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#0a0a2e]">{item.item_number}</h1>
          <div className="text-sm text-slate-600 mt-1">{item.manufacturer_name} — {item.product_code} {item.model} · SN {item.serial_number || "-"}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={item.current_status} className="text-sm" />
          <Button data-testid="item-print-label" variant="outline" onClick={() => openBinary(`/repairs/items/${item.id}/label.png`)}><FileDown className="w-4 h-4 mr-1" /> Etichetta QR</Button>
          {hasRole("reception","warehouse_tech","customer_service") && (
            <Button data-testid="item-change-status" onClick={() => setStatusDlg(true)} className="ama-btn-primary rounded-full"><PenSquare className="w-4 h-4 mr-1" /> Cambia stato</Button>
          )}
        </div>
      </div>

      <Card className="ama-card border-0 shadow-none">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Cliente" value={item.customer_name} />
            <Field label="Divisione" value={item.division} />
            <Field label="Garanzia" value={item.warranty_status} />
            <Field label="Ubicazione" value={item.current_location_code || "-"} />
            <Field label="Codice" value={item.product_code} />
            <Field label="Modello" value={item.model} />
            <Field label="Barcode" value={item.barcode} />
            <Field label="MAC" value={item.mac_address} />
          </div>
          <div className="mt-4">
            <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Problema dichiarato dal cliente installatore</div>
            <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-xl p-4 text-[#0a0a2e] whitespace-pre-wrap">{item.declared_problem}</div>
          </div>
          {(item.problem_frequency || item.problem_occurrence || item.problem_conditions || item.tests_performed_by_installer || item.continuity || item.reproducibility) && (
            <div className="mt-4">
              <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Dettagli del problema dichiarato</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm rounded-xl border border-slate-200 p-4 bg-slate-50/50" data-testid="item-problem-details">
                {item.problem_frequency && <Field label="Frequenza" value={item.problem_frequency} />}
                {item.problem_occurrence && <Field label="Quando si manifesta" value={item.problem_occurrence} />}
                {item.problem_conditions && <Field label="Condizioni" value={item.problem_conditions} />}
                {item.tests_performed_by_installer && <Field label="Prove già effettuate" value={item.tests_performed_by_installer} />}
                {item.continuity && <Field label="Continuità/intermittenza" value={item.continuity} />}
                {item.reproducibility && <Field label="Riproducibilità" value={item.reproducibility} />}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="timeline" className="w-full">
        <TabsList>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Cronologia</TabsTrigger>
          <TabsTrigger value="diagnosis" data-testid="tab-diagnosis">Diagnosi & preventivo</TabsTrigger>
          <TabsTrigger value="intervention" data-testid="tab-intervention">Intervento & esito</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="pt-4">
          <Card className="ama-card border-0 shadow-none">
            <CardContent className="pt-6">
              <Timeline events={item.status_events || []} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="diagnosis" className="pt-4 space-y-4">
          <Card className="ama-card border-0 shadow-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Diagnosi del produttore</CardTitle>
              {hasRole("customer_service","warehouse_tech") && <Button data-testid="add-diagnosis-btn" variant="outline" onClick={() => setDiagDlg(true)}>Registra diagnosi</Button>}
            </CardHeader>
            <CardContent>
              {!item.manufacturer_diagnosis ? <div className="text-sm text-slate-500 italic">Nessuna diagnosi ancora registrata.</div> : (
                <div className="space-y-2 text-sm">
                  <Field label="Diagnosi" value={item.manufacturer_diagnosis.diagnosis_text} multiline />
                  <Field label="Intervento proposto" value={item.manufacturer_diagnosis.proposed_repair} multiline />
                  <Field label="Costo produttore" value={item.manufacturer_diagnosis.manufacturer_cost != null ? `€ ${Number(item.manufacturer_diagnosis.manufacturer_cost).toFixed(2)}` : "-"} />
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="ama-card border-0 shadow-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Preventivo cliente</CardTitle>
              {hasRole("customer_service","accounting") && <Button data-testid="add-quote-btn" variant="outline" onClick={() => setQuoteDlg(true)}>{q ? "Nuovo preventivo" : "Crea preventivo"}</Button>}
            </CardHeader>
            <CardContent>
              {!q ? <div className="text-sm text-slate-500 italic">Nessun preventivo generato.</div> : (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="Netto cliente" value={`€ ${Number(q.customer_price_net).toFixed(2)}`} />
                    <Field label="IVA" value={`${q.vat_rate}%`} />
                    <Field label="Totale" value={`€ ${Number(q.customer_price_gross).toFixed(2)}`} />
                    <Field label="Tempi stimati" value={`${q.estimated_days} giorni`} />
                    <Field label="Scadenza" value={q.expiry_date || "-"} />
                    <Field label="Stato" value={q.approval_status === "approved" ? "Autorizzato" : q.approval_status === "rejected" ? "Rifiutato" : "In attesa"} />
                  </div>
                  {approvalUrl && (
                    <Alert className="bg-fuchsia-50 border-fuchsia-100">
                      <LinkIcon className="w-4 h-4" />
                      <AlertTitle>Link autorizzazione cliente</AlertTitle>
                      <AlertDescription>
                        <div className="font-mono text-xs break-all">{approvalUrl}</div>
                        <div className="flex gap-2 mt-2">
                          <Button data-testid="copy-approval-link" variant="outline" size="sm" onClick={copyLink}><Copy className="w-4 h-4 mr-1" /> Copia</Button>
                          <Button data-testid="open-approval-link" variant="outline" size="sm" onClick={() => window.open(approvalUrl, "_blank")}><ExternalLink className="w-4 h-4 mr-1" /> Apri</Button>
                          <Button data-testid="whatsapp-approval-link" variant="outline" size="sm" onClick={openWhatsapp}><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quotation attachments */}
          <Card className="ama-card border-0 shadow-none" data-testid="quotation-attachments-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">Documenti allegati al preventivo</CardTitle>
                <div className="text-xs text-slate-500">PDF, JPG, PNG, WebP, DOCX (max 15MB per file).</div>
              </div>
              {hasRole("customer_service","accounting") && q && (
                <label className="inline-flex items-center gap-1 cursor-pointer bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:border-[#d63384] text-sm">
                  <Upload className="w-4 h-4" />
                  {attachmentUploading ? "Caricamento…" : "Allega documento"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,image/*,application/pdf" className="hidden" data-testid="attachment-upload-input"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f, false); e.target.value = ""; }} />
                </label>
              )}
            </CardHeader>
            <CardContent>
              {!q ? (
                <div className="text-sm text-slate-500 italic">Crea prima un preventivo per allegare documenti.</div>
              ) : attachments.length === 0 ? (
                <div className="text-sm text-slate-500 italic">Nessun documento allegato al preventivo.</div>
              ) : (
                <div className="space-y-2" data-testid="attachments-list">
                  {attachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`attachment-row-${a.id}`}>
                      <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#0a0a2e] truncate">{a.display_name || a.filename}</div>
                        <div className="text-[11px] text-slate-500 truncate">{a.filename} · {a.file_type} · {((a.size || 0) / 1024).toFixed(0)} KB</div>
                        <div className="mt-0.5">
                          {a.customer_visible
                            ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-transparent">Visibile al cliente</Badge>
                            : <Badge variant="outline" className="text-[10px]">Solo uso interno</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="outline" data-testid={`attachment-download-${a.id}`} onClick={() => downloadAtt(a)}><FileDown className="w-3.5 h-3.5" /></Button>
                        {hasRole("administrator","customer_service") && (
                          <>
                            <Button size="sm" variant="outline" data-testid={`attachment-rename-${a.id}`} onClick={() => renameAtt(a)} title="Rinomina"><PenSquare className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="outline" data-testid={`attachment-visibility-${a.id}`} onClick={() => toggleAttVisibility(a)} title={a.customer_visible ? "Rendi interno" : "Rendi visibile al cliente"}>
                              {a.customer_visible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </Button>
                            <Button size="sm" variant="outline" data-testid={`attachment-delete-${a.id}`} onClick={() => deleteAtt(a)} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="intervention" className="pt-4">
          <Card className="ama-card border-0 shadow-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Intervento eseguito ed esito finale</CardTitle>
              {hasRole("warehouse_tech","customer_service") && <Button data-testid="add-intervention-btn" variant="outline" onClick={() => setInterDlg(true)}>Registra intervento</Button>}
            </CardHeader>
            <CardContent>
              {!item.intervention ? <div className="text-sm text-slate-500 italic">Nessun intervento ancora registrato.</div> : (
                <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <Field label="Tipo intervento" value={item.intervention.intervention_type} />
                  <Field label="Descrizione" value={item.intervention.description} multiline />
                  <Field label="Esito finale" value={item.intervention.final_outcome} />
                  <Field label="Seriale sostituito" value={item.replacement_serial_number || "-"} />
                  <Field label="Seriale restituito" value={item.returned_serial_number || item.serial_number} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                  <Button data-testid="outcome-generate-link" variant="outline" size="sm"
                    onClick={async () => {
                      try {
                        const r = await api.post(`/repairs/items/${item.id}/outcome-link`, { expires_days: 30 });
                        const url = `${window.location.origin}/esito/${r.data.token}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Link esito creato e copiato negli appunti (scade in 30 giorni).");
                      } catch (e) { toast.error("Errore creazione link"); }
                    }}><LinkIcon className="w-4 h-4 mr-1" /> Copia link esito</Button>
                  <Button data-testid="outcome-download-pdf" variant="outline" size="sm"
                    onClick={() => openBinary(`/repairs/items/${item.id}/outcome.pdf?ts=${Date.now()}`)}><FileDown className="w-4 h-4 mr-1" /> Scarica PDF esito</Button>
                  <Button data-testid="outcome-whatsapp" variant="outline" size="sm"
                    onClick={async () => {
                      try {
                        const r = await api.post(`/repairs/items/${item.id}/outcome-link`, { expires_days: 30 });
                        const url = `${window.location.origin}/esito/${r.data.token}`;
                        const msg = `Gentile Cliente, la sua pratica AMA Sicurezza ${item.item_number} è stata completata. Esito e riepilogo: ${url}`;
                        const phone = (c?.delivering_phone || "").replace(/[^0-9]/g, "");
                        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
                      } catch (e) { toast.error("Errore"); }
                    }}><MessageCircle className="w-4 h-4 mr-1" /> Apri su WhatsApp</Button>
                  <Button data-testid="outcome-revoke" variant="outline" size="sm"
                    onClick={async () => {
                      if (!window.confirm("Revocare tutti i link esito attivi per questa riparazione?")) return;
                      try { const r = await api.post(`/repairs/items/${item.id}/outcome-link/revoke`); toast.success(`${r.data.revoked} link revocati.`); }
                      catch { toast.error("Errore"); }
                    }}>Revoca link</Button>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <Dialog open={statusDlg} onOpenChange={setStatusDlg}>
        <DialogContent><DialogHeader><DialogTitle>Cambia stato prodotto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nuovo stato</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="new-status-select"><SelectValue placeholder="Seleziona stato" /></SelectTrigger>
                <SelectContent className="max-h-72">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nota interna</Label><Textarea data-testid="status-internal-note" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} /></div>
            <div><Label>Nota visibile al cliente</Label><Textarea data-testid="status-customer-note" value={statusCustomerNote} onChange={(e) => setStatusCustomerNote(e.target.value)} /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setStatusDlg(false)}>Annulla</Button><Button data-testid="status-save" className="ama-btn-primary" onClick={doStatus}>Salva</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={diagDlg} onOpenChange={setDiagDlg}>
        <DialogContent><DialogHeader><DialogTitle>Diagnosi del produttore</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Diagnosi tecnica</Label><Textarea data-testid="diag-text" value={diag.diagnosis_text} onChange={(e) => setDiag({ ...diag, diagnosis_text: e.target.value })} /></div>
            <div><Label>Intervento proposto</Label><Textarea value={diag.proposed_repair} onChange={(e) => setDiag({ ...diag, proposed_repair: e.target.value })} /></div>
            <div><Label>Costo produttore (€)</Label><Input type="number" step="0.01" value={diag.manufacturer_cost} onChange={(e) => setDiag({ ...diag, manufacturer_cost: parseFloat(e.target.value || "0") })} /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDiagDlg(false)}>Annulla</Button><Button data-testid="diag-save" className="ama-btn-primary" onClick={doDiag}>Salva</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={quoteDlg} onOpenChange={setQuoteDlg}>
        <DialogContent><DialogHeader><DialogTitle>Preventivo cliente</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Costo produttore</Label><Input type="number" step="0.01" value={quote.manufacturer_cost} onChange={(e) => setQuote({ ...quote, manufacturer_cost: +e.target.value })} /></div>
            <div><Label>Spedizione AMA</Label><Input type="number" step="0.01" value={quote.ama_shipping_cost} onChange={(e) => setQuote({ ...quote, ama_shipping_cost: +e.target.value })} /></div>
            <div><Label>Manodopera AMA</Label><Input type="number" step="0.01" value={quote.ama_labour_cost} onChange={(e) => setQuote({ ...quote, ama_labour_cost: +e.target.value })} /></div>
            <div><Label>Altri costi</Label><Input type="number" step="0.01" value={quote.other_costs} onChange={(e) => setQuote({ ...quote, other_costs: +e.target.value })} /></div>
            <div><Label>Prezzo cliente (netto)</Label><Input data-testid="quote-price-net" type="number" step="0.01" value={quote.customer_price_net} onChange={(e) => setQuote({ ...quote, customer_price_net: +e.target.value })} /></div>
            <div><Label>IVA %</Label><Input type="number" step="0.5" value={quote.vat_rate} onChange={(e) => setQuote({ ...quote, vat_rate: +e.target.value })} /></div>
            <div><Label>Tempi stimati (gg)</Label><Input type="number" value={quote.estimated_days} onChange={(e) => setQuote({ ...quote, estimated_days: +e.target.value })} /></div>
            <div><Label>Scadenza</Label><Input placeholder="30/09/2026" value={quote.expiry_date} onChange={(e) => setQuote({ ...quote, expiry_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Condizioni</Label><Textarea value={quote.conditions} onChange={(e) => setQuote({ ...quote, conditions: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setQuoteDlg(false)}>Annulla</Button><Button data-testid="quote-save" className="ama-btn-primary" onClick={doQuote}>Genera preventivo e link cliente</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={interDlg} onOpenChange={setInterDlg}>
        <DialogContent><DialogHeader><DialogTitle>Intervento eseguito ed esito</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tipo intervento</Label>
              <Select value={inter.intervention_type} onValueChange={(v) => setInter({ ...inter, intervention_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="repaired">Riparato</SelectItem>
                  <SelectItem value="replaced">Sostituito</SelectItem>
                  <SelectItem value="firmware">Aggiornamento firmware</SelectItem>
                  <SelectItem value="no_repair">Nessuna riparazione</SelectItem>
                  <SelectItem value="other">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Descrizione</Label><Textarea value={inter.description} onChange={(e) => setInter({ ...inter, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Seriale sostituito (nuovo)</Label><Input value={inter.replacement_serial_number} onChange={(e) => setInter({ ...inter, replacement_serial_number: e.target.value })} /></div>
              <div><Label>Seriale restituito</Label><Input value={inter.returned_serial_number} onChange={(e) => setInter({ ...inter, returned_serial_number: e.target.value })} /></div>
            </div>
            <div><Label>Esito finale</Label>
              <Select value={inter.final_outcome} onValueChange={(v) => setInter({ ...inter, final_outcome: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="funzionante">Funzionante</SelectItem>
                  <SelectItem value="funzionante dopo riparazione">Funzionante dopo riparazione</SelectItem>
                  <SelectItem value="sostituito">Sostituito</SelectItem>
                  <SelectItem value="non riparabile">Non riparabile</SelectItem>
                  <SelectItem value="difetto non riscontrato">Difetto non riscontrato</SelectItem>
                  <SelectItem value="restituito senza riparazione">Restituito senza riparazione</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setInterDlg(false)}>Annulla</Button><Button data-testid="inter-save" className="ama-btn-primary" onClick={doIntervention}>Salva</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, multiline = false }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm text-[#0a0a2e] font-medium ${multiline ? "whitespace-pre-wrap" : ""}`}>{value || "-"}</div>
    </div>
  );
}
